import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, DataTable, StatusPill } from "@/components/common";
import { useApp } from "@/lib/store";
import { fmtDate, shortId } from "@/lib/utils";

interface Pack { id: string; name: string; semver: string; signature_ok: boolean; created_at: string }
interface Install { id: string; pack: string; applied: unknown; installed_at: string }

export function Packs() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [semver, setSemver] = useState("0.1.0");

  const packs = useQuery<{ results: Pack[] }>({
    queryKey: ["packs", ws],
    queryFn: () => rawFetch(`/packs/packs/?ordering=-created_at&page_size=100${ws ? `&workspace=${ws}` : ""}`),
  });
  const installs = useQuery<{ results: Install[] }>({
    queryKey: ["pack-installs", ws],
    queryFn: () => rawFetch(`/packs/installs/?ordering=-installed_at&page_size=20${ws ? `&workspace=${ws}` : ""}`),
  });

  const build = useMutation({
    mutationFn: () => rawFetch("/packs/packs/build/", {
      method: "POST",
      body: JSON.stringify({ name, semver, workspace_id: ws }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["packs"] }),
  });
  const verify = useMutation({
    mutationFn: (id: string) => rawFetch(`/packs/packs/${id}/verify/`),
  });
  const install = useMutation({
    mutationFn: (id: string) => rawFetch(`/packs/packs/${id}/install/`, {
      method: "POST",
      body: JSON.stringify({ workspace_id: ws, overwrite: false }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pack-installs"] }),
  });

  return (
    <>
      <PageHeader title="Domain Packs"
                  subtitle="Signed, versioned bundles of ontology + pipelines + agents + retrieval profile. Build from one workspace, install into many." />

      <div className="card mb-4 border-accent/30 text-xs text-muted">
        <div className="mb-1 text-sm font-semibold text-fg">When to use a Pack</div>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong className="text-fg">Build &amp; sign:</strong> Snapshot the <em>current workspace</em> &mdash; published ontology version, agents, pipelines, retrieval profile &mdash; into a single signed artifact you can ship.</li>
          <li><strong className="text-fg">Verify:</strong> Confirms the signature and manifest hash so the receiver knows the bundle hasn’t been tampered with.</li>
          <li><strong className="text-fg">Install:</strong> Materialises the pack into <em>another workspace</em> &mdash; ideal for promoting from dev &rarr; staging &rarr; prod, or for distributing to partner tenants.</li>
        </ul>
        <div className="mt-2 text-[11px]">
          Different from the one-click <Link to="/marketplace" className="text-accent underline">Marketplace</Link> (which seeds a fresh vertical from our catalog) and from{" "}
          <Link to="/ontology" className="text-accent underline">Ontology Standards</Link> (which seed only the schema). Packs carry the <em>whole runtime configuration</em>.
        </div>
      </div>

      <div className="card mb-4">
        <div className="mb-2 text-xs uppercase text-muted">Build pack from current workspace</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_auto]">
          <input className="input" placeholder="pack name"
                 value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" placeholder="0.1.0"
                 value={semver} onChange={(e) => setSemver(e.target.value)} />
          <button className="btn-primary" disabled={!ws || !name || build.isPending}
                  onClick={() => build.mutate()}>Build & sign</button>
        </div>
        {build.isError && <div className="mt-2"><ErrorBox error={build.error} /></div>}
      </div>

      <h2 className="mb-2 text-sm font-medium text-muted">Packs</h2>
      {packs.isError && <ErrorBox error={packs.error} />}
      <DataTable
        rows={packs.data?.results ?? []}
        cols={[
          { header: "Name",    cell: (p) => p.name },
          { header: "Version", cell: (p) => <code className="font-mono text-xs">{p.semver}</code> },
          { header: "Signed",  cell: (p) => <StatusPill status={p.signature_ok ? "ok" : "invalid"} /> },
          { header: "Created", cell: (p) => fmtDate(p.created_at) },
          { header: "",        width: "1%", cell: (p) => (
              <div className="flex gap-1">
                <button className="btn" onClick={() => verify.mutate(p.id)}>Verify</button>
                <button className="btn-primary" disabled={!ws}
                        onClick={() => install.mutate(p.id)}>Install</button>
              </div>
            ),
          },
        ]}
      />
      {verify.data != null && (
        <pre className="mt-2 rounded border border-border bg-bg p-2 text-xs">
          {JSON.stringify(verify.data, null, 2)}
        </pre>
      )}
      {install.isError && (
        <div className="mt-2">
          <ErrorBox error={install.error} />
        </div>
      )}

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Installs</h2>
      {installs.isError && <ErrorBox error={installs.error} />}
      <DataTable
        rows={installs.data?.results ?? []}
        cols={[
          { header: "ID",      cell: (i) => <code className="font-mono text-xs">{shortId(i.id)}</code> },
          { header: "Pack",    cell: (i) => <code className="font-mono text-xs">{shortId(i.pack)}</code> },
          { header: "Status",  cell: (i) => <StatusPill status={i.applied === false ? "failed" : "applied"} /> },
          { header: "Installed", cell: (i) => fmtDate(i.installed_at) },
        ]}
      />
    </>
  );
}
